import { Fragment, useRef, useState, type ReactNode } from "react"
import { Button, Flex, Modal } from "antd"
import { POINT_ICON_URL, getVideoChannelLabel } from "~/common/constants"
import {
  MIN_VIDEO_GENERATION_POINT_COST,
  getVideoGenerationPointSavingOptions,
  type VideoGenerationPointCostParams,
  type VideoGenerationPointSavingOptions,
} from "~/common/billing/pointCosts"
import { useSessionStore } from "~/store/session"
import {
  ensureSufficientPoints,
  usePointBalanceStore,
} from "~/store/pointBalance"
import { createPointActionRunner } from "~/common/billing/pointAction"
import styles from "./index.css"
import {
  Text,
  RechargeUpgradeModal,
  type RechargeModalTabKey,
} from "~/components"

type PointCostTextProps = {
  cost: number
  prefix?: string
}

const getPointSavingSuggestions = ({
  canChangeModel,
  canChangeResolution,
  canShortenDuration,
}: VideoGenerationPointSavingOptions) => {
  const suggestions: ReactNode[] = []

  if (canChangeModel && canChangeResolution) {
    suggestions.push(
      <>
        <strong>切换视频模型或清晰度</strong>（可能会降低视频效果）
      </>,
    )
  } else if (canChangeModel) {
    suggestions.push(
      <>
        <strong>切换视频模型</strong>（可能会降低视频效果）
      </>,
    )
  } else if (canChangeResolution) {
    suggestions.push(
      <>
        <strong>切换视频清晰度</strong>（可能会降低视频效果）
      </>,
    )
  }

  if (canShortenDuration) {
    suggestions.push(<strong>缩短视频时长</strong>)
  }

  return suggestions
}

export const PointCostText = ({
  cost,
  prefix = "预计消耗",
}: PointCostTextProps) => {
  return (
    <Text className={styles.pointCost}>
      {prefix}
      <img
        alt=""
        className={styles.pointIcon}
        src={POINT_ICON_URL}
      />
      <Text color="primary">{cost}</Text>
      积点
    </Text>
  )
}

export const usePointBalanceGuard = (
  constraints?: Partial<VideoGenerationPointSavingOptions>,
) => {
  const constraintsRef = useRef(constraints)
  constraintsRef.current = constraints
  const [guard] = useState(
    () => (cost: number, videoOptions?: VideoGenerationPointCostParams) =>
      ensureSufficientPoints(cost, videoOptions, constraintsRef.current),
  )
  const [runPointAction] = useState(() => createPointActionRunner(guard))

  return { runPointAction, ensureSufficientPoints: guard }
}

export const PointBalanceGuard = () => {
  const pointBalance = useSessionStore(
    (state) => state.user?.balance_info?.available_amount || 0,
  )
  const isProfessional = useSessionStore((state) => state.isProfessional)
  const insufficientOpen = usePointBalanceStore((state) => state.open)
  const insufficientVideoGeneration = usePointBalanceStore(
    (state) => state.videoOptions,
  )
  const constraints = usePointBalanceStore((state) => state.constraints)
  const close = usePointBalanceStore((state) => state.close)
  const [rechargeOpen, setRechargeOpen] = useState(false)
  const [rechargeTab, setRechargeTab] = useState<RechargeModalTabKey>("upgrade")

  const openRechargeModal = (tab: RechargeModalTabKey) => {
    setRechargeTab(tab)
    close()
    setRechargeOpen(true)
  }

  const pointSavingOptions = insufficientVideoGeneration
    ? getVideoGenerationPointSavingOptions(insufficientVideoGeneration)
    : undefined
  const pointSavingSuggestions = pointSavingOptions
    ? getPointSavingSuggestions({ ...pointSavingOptions, ...constraints })
    : []
  const shouldShowPointSavingGuide = Boolean(
    insufficientVideoGeneration && pointSavingOptions,
  )
  const shouldShowPointSavingSuggestions =
    pointBalance >= MIN_VIDEO_GENERATION_POINT_COST &&
    pointSavingSuggestions.length > 0

  const insufficientContent =
    insufficientVideoGeneration && pointSavingOptions ? (
      <Flex
        gap={4}
        vertical
      >
        <Text>
          当前生视频配置：
          <Text strong>
            {getVideoChannelLabel(insufficientVideoGeneration.model)} ·{" "}
            {insufficientVideoGeneration.resolution.toUpperCase()} ·{" "}
            {insufficientVideoGeneration.duration} 秒
          </Text>
          ，预计消耗 <Text strong>{insufficientVideoGeneration.cost} 积点</Text>
          ，当前剩余 <Text strong>{pointBalance} 积点</Text>，
          {shouldShowPointSavingSuggestions ? "积点不足" : "无法完成本次任务"}
        </Text>
        <Text>
          {shouldShowPointSavingSuggestions ? (
            <>
              你可以
              {pointSavingSuggestions.map((suggestion, index) => (
                <Fragment key={index}>
                  {index > 0 ? "、" : null}
                  {suggestion}
                </Fragment>
              ))}
              ，降低积点消耗；也可<strong>充值积点</strong>后继续生成
            </>
          ) : (
            <>
              你可以<strong>充值积点</strong>后继续生成
            </>
          )}
        </Text>
      </Flex>
    ) : (
      <Text>
        {isProfessional
          ? "当前积点余额不足，无法完成本次任务，请先单独充值积点"
          : "当前积点余额不足，无法完成本次任务，请先升级版本获取更多积点或单独充值积点"}
      </Text>
    )

  return (
    <>
      <Modal
        centered
        open={insufficientOpen}
        title="提示"
        width={shouldShowPointSavingGuide ? 500 : 320}
        onCancel={() => close()}
        footer={
          <Flex
            justify="flex-end"
            gap={8}
          >
            <Button onClick={() => close()}>
              {shouldShowPointSavingGuide ? "已了解" : "取消"}
            </Button>
            {isProfessional ? (
              <Button
                type="primary"
                onClick={() => openRechargeModal("recharge")}
              >
                充值积点
              </Button>
            ) : (
              <Button
                type="primary"
                onClick={() => openRechargeModal("upgrade")}
              >
                升级版本/充值积点
              </Button>
            )}
          </Flex>
        }
      >
        {insufficientContent}
      </Modal>
      <RechargeUpgradeModal
        defaultTab={rechargeTab}
        open={rechargeOpen}
        onCancel={() => setRechargeOpen(false)}
      />
    </>
  )
}
